import React, { Component } from "react";
import PropTypes from "prop-types";
import {TableGrid} from "@terraincognita/ui-core";
import _groupBy from "lodash/groupBy";
import _sortBy from "lodash/sortBy";
import { formatTimestamp } from "utils/helpers/timestampHelper";
import { formatDistanceToNow } from "date-fns";
import { getDisplayStatus, getStatusLabelForHistory } from "./publishStatusHelper";

export class PublishHistory2 extends Component {
  constructor(props) {
    super(props);
    this.handleViewDetails = this.handleViewDetails.bind(this);
  }

  handleViewDetails(publishId) {
    // Open the publish status dialog for this publishId
    if (this.props.handleViewDetailsClick) {
      this.props.handleViewDetailsClick(publishId);
    }
  }

  /**
   * Group history entries by publishId and get the most recent entry for each publish
   */
  groupHistoryByPublishId(history) {
    if (!history || !Array.isArray(history) || history.length === 0) {
      return [];
    }

    // Group by publishId
    const grouped = _groupBy(history, "publishId");

    // Convert to array and get the first entry (most recent timestamp) for each publish
    const publishGroups = Object.keys(grouped).map(publishId => {
      const entries = _sortBy(grouped[publishId], "timestamp");
      const firstEntry = entries[0];
      const lastEntry = entries[entries.length - 1];

      const bundleRecordEntry = entries.find(e => e.status === "bundle-record-created");
      const bundleId = bundleRecordEntry?.metadata?.bundleId || null;

      return {
        publishId,
        timestamp: firstEntry?.timestamp,
        status: lastEntry?.status,
        userID: firstEntry?.userID,
        firstname: firstEntry?.metadata?.firstname,
        lastname: firstEntry?.metadata?.lastname,
        metadata: lastEntry?.metadata || {},
        projectName: firstEntry?.metadata?.projectName || "Unknown Project",
        bundleId
      };
    });

    // Sort by timestamp, most recent first
    return _sortBy(publishGroups, "timestamp").reverse();
  }

  /**
   * Get status label for display
   * Uses centralized helper to get the step label based on the status
   * @param {String} status - The status from the publish history
   * @param {Object} metadata - The metadata object from the publish history entry
   * @returns {String} The status label, with "(cancelled)" appended if cancelledAt exists
   */
  getStatusLabel(status, metadata) {
    if (!status) return "";
    
    // Get the step label for the display status
    const displayStatus = getDisplayStatus(status);
    if (!displayStatus) return "";
    
    let label = getStatusLabelForHistory(status);
    
    if (metadata && metadata.error) {
      label = "Error: " + label;
    } else if (metadata && metadata.cancelledAt) {
      label += " (cancelled)";
    }
    
    return label;
  }

  getStatusLabelWithIcon(status, metadata) {
    const label = this.getStatusLabel(status, metadata);
    if (!label) return "";

    const iconSize = 16;
    const iconStyle = "vertical-align: middle; margin-right: 4px; margin-top: -2px;";
    const viewBox = "0 -960 960 960";

    // We can't use the icons from the icons folder because they
    // are not SVGs, and we need to use SVGs because the TableGrid
    // component doesn't support icons from the icons folder.
    if (metadata && metadata.error) {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" height="${iconSize}" width="${iconSize}" viewBox="${viewBox}" fill="#c62828" style="${iconStyle}"><path d="M480-280q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"/></svg>`;
      return svg + label;
    }

    if (status === "complete") {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" height="${iconSize}" width="${iconSize}" viewBox="${viewBox}" fill="#2e7d32" style="${iconStyle}"><path d="m424-296 282-282-56-56-226 226-114-114-56 56 170 170Zm56 216q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"/></svg>`;
      return svg + label;
    }

    return label;
  }

  /**
   * Get published by name from metadata
   */
  getPublishedByName(metadata) {
    if (metadata.firstname && metadata.lastname) {
      return `${metadata.firstname} ${metadata.lastname}`;
    }
    if (metadata.firstname) {
      return metadata.firstname;
    }
    if (metadata.lastname) {
      return metadata.lastname;
    }
    return "Unknown";
  }

  render() {
    const { installationData } = this.props;

    if (!installationData) {
      return null;
    }

    const publishHistory = installationData.publishHistory || [];
    const publishGroups = this.groupHistoryByPublishId(publishHistory);

    if (publishGroups.length === 0) {
      return null;
    }

    const mostRecentCompletePublishId = publishGroups.find(g => g.status === "complete")?.publishId;

    // Convert to TableGrid format
    const items = {};
    publishGroups.forEach((group, index) => {
      const dateStr = formatTimestamp(group.timestamp) + " (" + formatDistanceToNow(new Date(group.timestamp), { addSuffix: true }) + ")";
      const statusLabel = this.getStatusLabel(group.status, group.metadata);
      const projectName = group.projectName || "Unknown Project";
      const publishedBy = (group.firstname && group.lastname)
        ? `${group.firstname} ${group.lastname}`
        : (group.firstname || group.lastname || "Unknown");

      items[index + 1] = {
        id: index + 1,
        columns: {
          1: {
            id: 1,
            label: dateStr
          },
          2: {
            id: 2,
            label: projectName
          },
          3: {
            id: 3,
            label: publishedBy
          },
          4: {
            id: 4,
            label: this.getStatusLabelWithIcon(group.status, group.metadata),
            sortValue: statusLabel || ""
          }
        },
        publishId: group.publishId,
        bundleId: group.bundleId,
        status: group.status
      };
    });

    const header = {
      1: {
        id: 1,
        label: "Date"
      },
      2: {
        id: 2,
        label: "Project"
      },
      3: {
        id: 3,
        label: "Published By"
      },
      4: {
        id: 4,
        label: "Status"
      },
      5: {
        id: 5,
        label: ""
      }
    };

    const headerStyle = {
      backgroundColor: "#f2f2f2",
      fontWeight: 600
    };

    return (
      <div style={{ padding: "0 75px 65px 75px" }}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>Publishing History</h3>
        <TableGrid
          items={items}
          header={header}
          headerStyle={headerStyle}
          idNode="id"
          rowActions={(item) => {
            const actions = [{ value: "details", label: "Publish details" }];
            const isCurrentVersion = item.publishId === mostRecentCompletePublishId;
            if (item.bundleId && item.status === "complete" && !isCurrentVersion) {
              actions.push({ value: "restore", label: "Restore this version" });
            }
            return actions;
          }}
          onRowAction={(value, item) => {
            if (value === "details" && item.publishId) {
              this.handleViewDetails(item.publishId);
            } else if (value === "restore" && item.bundleId && this.props.onRevertClick) {
              this.props.onRevertClick(item.bundleId);
            }
          }}
          sortable
          multiSelectable={false}
        />
      </div>
    );
  }
}

PublishHistory2.propTypes = {
  installationData: PropTypes.object,
  projects: PropTypes.object,
  handleViewDetailsClick: PropTypes.func,
  onRevertClick: PropTypes.func
};

